// Fetches the latest CallAnalysis for a given call and live-invalidates when
// the server emits `call_analysis_ready` / `call_analysis_failed`. The hook
// returns `null` (with isLoading=false) for calls that have no analysis row
// yet, so the panel can render a "no analysis" state without flashing errors.

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CallAnalysisDTO } from '@marnic/shared';
import { api } from '../lib/api';
import { useSocket } from '../lib/socket';

export type AnalysisQueryResult =
  | { state: 'none' }
  | { state: 'analysis'; analysis: CallAnalysisDTO };

async function fetchAnalysis(callId: string): Promise<AnalysisQueryResult> {
  try {
    const data = await api.get<CallAnalysisDTO>(`/calls/${callId}/analysis`);
    return { state: 'analysis', analysis: data };
  } catch (err) {
    const code = (err as { status?: number; code?: string }).status;
    if (code === 404) return { state: 'none' };
    throw err;
  }
}

export function useCallAnalysis(callId: string | null) {
  const qc = useQueryClient();
  const socket = useSocket();
  const queryKey = ['call', callId, 'analysis'] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => fetchAnalysis(callId as string),
    enabled: !!callId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!socket || !callId) return;
    const onReady = (p: { callId: string }): void => {
      if (p.callId === callId) qc.invalidateQueries({ queryKey });
    };
    const onFailed = (p: { callId: string }): void => {
      if (p.callId === callId) qc.invalidateQueries({ queryKey });
    };
    socket.on('call_analysis_ready', onReady);
    socket.on('call_analysis_failed', onFailed);
    return () => {
      socket.off('call_analysis_ready', onReady);
      socket.off('call_analysis_failed', onFailed);
    };
  }, [socket, callId, qc, queryKey]);

  return query;
}

export async function retryCallAnalysis(callId: string): Promise<{ id: string; version: number }> {
  return api.post(`/calls/${callId}/analysis/retry`);
}

/** Swap agent ↔ prospect labels on the latest succeeded transcript and re-run
 * only the LLM passes (no STT cost). Supervisor/admin only on the server. */
export async function flipCallAnalysisAttribution(
  callId: string,
): Promise<{ id: string; version: number; status: string }> {
  return api.post(`/calls/${callId}/analysis/flip-attribution`);
}
